import _ from "lodash";
import api from "CommonCore/api";
import moment from "moment";
import {
  setupLookups,
} from "App/shk-form-tab";
import {
  dateRange,
  formatDate,
  formatNumber,
} from "CommonCore/l10n";
import {
  Spinner,
  Well,
} from "CommonComponents/bricks";
import {
  asyncOperation,
  inline,
} from "CommonState/action-helpers";
import apifetch from "CommonCore/apifetch";

import {
  ASTMODUSKEY,
  config,
  istArtTarifvertrag,
  ROLLEN,
  VERTRAGSARTEN,
  VERTRAGSARTEN_LEHRAUFTRAG,
  VERTRAGSARTEN_SHK,
  VERTRAGSARTEN_VORTRAG,
} from "App/constants";
import TR from "Common/translate";
import {
  VERTRAGSARTEN_TARIF,
} from "../constants";

// Die Editoren bekommen über die allgemeine Property additionalInjects netterweise die Entity (Maßnahme) übergeben
const defaultVon = { inject: ["additionalInjects"], generator: entity => entity.m_gueltigab };
const defaultBis = { inject: ["additionalInjects"], generator: entity => entity.m_gueltigbis };

// Wenn WHK oder WHK, dann keine Stellenausschreibung
export const ohneAusschreibung = mn => {
  return mn.m_artid != VERTRAGSARTEN.SHK;
};

const istAntragsteller = {
  inject: ["user"],
  generator: user => () => user?.ro_id == ROLLEN.ROLLE_ANTRAGSTELLER,
};

/*
 * const istTarif_additional = {
 *   inject: ['additionalInjects'],
 *   generator: (entity) => {
 *     return istArtTarifvertrag(entity.m_artid)
 *   }
 * }
 */

/*
 * const keinTarif_additional = {
 *   inject: ['additionalInjects'],
 *   generator: (entity) => !istArtTarifvertrag(entity.m_artid)
 * }
 */

const keinTarif = {
  inject: ["istTarif"],
  generator: istTarif => () => !istTarif,
};

const keinHilfskraft = {
  inject: ["flag_antragstellermodus"],
  generator: modus => () => modus != ASTMODUSKEY.Hilfskraft,
};

export const schemaMassnahme = {
  displayName: "Maßnahme",
  formatWithDefinitiveArticle: a => (`die Maßnahme '${ a.m_bez }'`),
  toUserString: a => (`Maßnahme '${ a.m_bez }'`),
  defaultSort: [{ field: "m_gueltigab" }],
  table: [
    {
      field: "m_pbpid",
      display: "PBP-ID   ",
      required: true,
      sortSelector: value => value,
      selector: value => value && "PBP-" + value,
      maxlength: 10,
      sortable: true,
    },
    {
      field: "m_bez",
      display: "Bezeichnung",
      required: true,
      maxlength: 200,
      sortable: true,
    },
    {
      field: "antrag",
      display: "Antrag",
      hideFromDetail: true,
      hideFromEditor: () => true,
      sortable: true,
      selector: value => value && value.a_nr,
      filter: {
        filterbox: "text",
        subquery: {
          fields: { antrag: { alias: "a_nr" } },
          generator: entity => {
            return entity && entity.antrag;
          },
        },
      },
    },
    {
      field: "antragstatus",
      display: "Antragstatus",
      hideFromDetail: true,
      hideFromEditor: () => true,
      sortable: true,
      inject: ["k_antragstatusLookup"],
      selector: lookup => (value, entity) => entity && entity.antrag && lookup && lookup[entity.antrag.a_asid] && lookup[entity.antrag.a_asid].as_bez,
      filter: {
        filterbox: {
          inject: ["k_antragstatusLookup"],
          choices: lookup => _.sortBy(_.map(lookup, l => l.as_bez)),
        },
        subquery: {
          fields: { antragstatus: { alias: "as_bez" } },
          inject: ["k_antragstatusLookup"],
          generator: lookup => entity => {
            return entity && entity.antrag && lookup && lookup[entity.antrag.a_asid];
          },
        },
      },
    },
    {
      field: "m_fakultaet_eid",
      display: "Fakultät / Zentrale Einrichtung",
      type: "select",
      choices: lookup => _.map(_.filter(lookup, e => e.e_master_eid == e.e_id), l => ({ value: l.e_id, label: l.e_kbez })),
      inject: ["k_einrichtungLookup"],
      selector: lookup => id => id && lookup && lookup[id].e_kbez,
      filter: {
        filterbox: {
          inject: ["k_einrichtungLookup"],
          choices: lookup => _.sortBy(_.map(_.filter(lookup, e => e.e_master_eid == e.e_id), l => l.e_kbez)),
        },
        subquery: {
          inject: ["k_einrichtungLookup"],
          fields: { m_fakultaet_eid: { alias: "e_kbez" } },
          generator: lookup => entity => {
            return lookup && entity && lookup[entity.m_fakultaet_eid];
          },
        },
      },
    },

    {
      field: "m_eid",
      display: "Einrichtung",
      type: "select",
      inject: ["k_einrichtungLookup", "getEntity"],
      // Die getEntity-Funktion (Property) wird in das das Schema nutzende EditPanel injected (in mn-detail-page)
      choices: (lookup, getEntity) => getEntity && lookup && _.map(_.filter(lookup, e => e.e_master_eid == getEntity.m_fakultaet_eid), l => ({ value: l.e_id, label: l.e_kbez })),
      selector: lookup => id => id && lookup && lookup[id].e_kbez,
      filter: {
        filterbox: "text",
        subquery: {
          fields: { m_eid: { alias: "e_kbez" } },
          inject: ["k_einrichtungLookup"],
          generator: lookup => entity => {
            return lookup && entity && lookup[entity.m_eid];
          },
        },
      },
      required: true,
      sortable: true,
    },
    {
      field: "m_e_asp",
      display: "Einrichtungsverantwortlich",
      maxlenght: 100,
      sortable: true,
      initallyHidden: true,
    },

    {
      field: "m_asp",
      display: TR("label_MassnahmeErsteller"),
      maxlenght: 100,
      sortable: true,
    },

    {
      field: "m_gueltigab",
      display: "Geplanter Beginn",
      type: "date",
      sortable: true,
      required: true,
    },
    {
      field: "m_gueltigbis",
      display: "Geplantes Ende",
      type: "date",
      sortable: true,
    },
    {
      field: "m_endefix",
      display: "Zeitraum",
      hideFromList: true,
      initallyHidden: true,
      choices: [{ value: false, label: "Zeitraum fix" }, { value: true, label: "Ende fix" }],
      selector: value => value === undefined ? "" : value == true ? "Ende fix" : "Zeitraum fix",
      hideFromDetail: mn => !mn.m_gueltigbis,
      readonly: true,
    },
    {
      field: "m_tbeid",
      display: "Befristungsgrund",
      lookup: "k_tarifbefristet.select.tbe_id.tbe_bez",
      hideFromDetail: keinTarif,
      hideFromList: keinTarif,
    },
    {
      field: "finanzierung",
      display: "Finanzierung",
      sortable: true,
      hideFromDetail: true,
      hideFromEditor: () => true,
      selector: (value, entity) => _.map(entity.massnahmefinanz, f => formatKonto({ kostenstelle: f.mfi_kostenstelle, budgetkonto: f.mfi_budgetkonto }) + (f.mfi_prozent != 100 ? ` ${ f.mfi_prozent }%` : "")).join(", "),
      filter: {
        filterbox: "text",
        subquery: {
          fields: { finanzierung: { alias: "value" } },
          generator: entity => {
            const result = _.map(entity.massnahmefinanz, f => formatKonto({ kostenstelle: f.mfi_kostenstelle, budgetkonto: f.mfi_budgetkonto }) + (f.mfi_prozent != 100 ? ` ${ f.mfi_prozent }%` : ""));
            return { value: result.join(", ") };
          },
        },
      },
    },
    {
      field: "m_artid",
      display: "Vertragsart",
      lookup: "k_vertragsart.select.art_id.art_kbez",
      required: true,
      hideFromDetail: istAntragsteller,
      hideFromList: istAntragsteller,
    },
    {
      field: "m_vorgesehen",
      display: "Vorgesehen für",
      helpText: "Bitte geben Sie hier den ZIM Account der vorgesehenen Person an. Falls kein ZIM Account vorhanden ist, tragen Sie die E-Mail-Adresse ein, mit der die Person sich am e-Portal registriert",
      sortable: true,
    },
    {
      field: "m_name",
      display: "Name",
      sortable: true,
    },
    {
      field: "m_vorname",
      display: "Vorname",
      sortable: true,
    },
    {
      field: "massnahmepaz",
      // type: 'array',
      display: "Arbeitszeiten",
      selector: array => _.map(array, item => `${ dateRange(item.mp_gueltigab, item.mp_gueltigbis) } ${ item.mp_std } Std.`).join(", "),
      specialEditor: true,
      hideFromDetail: true,
    },

    /*
     *{
     *  field: 'm_veroeffentlicht',
     *  display: 'Veröffentlicht',
     *  type: 'checkbox',
     *  sortable: true,
     *  readonly: true,
     *  hideFromDetail: true,
     *  hideFromList: true,
     *},
     */
    {
      field: "m_staveroeffentlicht",
      display: "Stellenausschreibung veröffentlicht",
      type: "checkbox",
      readonly: true,
      hideFromDetail: entity => ohneAusschreibung(entity),
      hideFromList: keinHilfskraft,
    },
    {
      field: "m_stavon",
      display: "Veröffentlicht von",
      type: "date",
      readonly: true,
      hideFromDetail: entity => !entity.m_staveroeffentlicht,
      hideFromList: keinHilfskraft,
    },
    {
      field: "m_stabis",
      display: "Veröffentlicht bis",
      type: "date",
      readonly: true,
      hideFromDetail: entity => !entity.m_staveroeffentlicht,
      hideFromList: keinHilfskraft,
    },
    {
      field: "m_staort",
      display: "Ort",
      readonly: true,
      hideFromDetail: entity => !entity.m_staveroeffentlicht,
      hideFromList: keinHilfskraft,
      filter: {
        filterbox: "text",
        subquery: {
          fields: { m_staort: { alias: "ort" } },
          generator: entity => {
            return entity && entity.m_staort && _.isArray(entity.m_staort)
? _.map(entity.m_staort, ort => {
  return { ort: ort };
})
: [];
          },
        },
      },
    },
    {
      field: "m_kommentarpersonalabteilung",
      display: "Kommentar für Personalabteilung",
      type: "textarea",
      readonly: true,
      hideFromDetail: entity => !entity.m_kommentarpersonalabteilung,
    },

  ],
};


export const validateMassnahme = (user, massnahme) => {
  // PBP-902: Validierung Einstellungstermin

  const messages = [];

  /*
   *Validierung 1 Frist bis Vertragsbeginn
   *    beim Speichern der Maßnahme, sofern das Flag "veröffentlicht" = True ist
   *    beim Annehmen einer Maßnahme in einen Antrag (Aktion: "Antrag stellen" nach Auswahl der Maßnahme)
   *    beim erstmaligen Einreichen eines Antrags (Eingangsdatum noch leer)
   *Frühester Beginn der frühesten Maßnahme muss >= x Wochen in der Zukunft liegen. Außnahme bei Änderungsmaßnahme.
   *Wenn nicht: Hinweismeldung
   *    (Dekan) beim Speichern der Maßnahme: "Der Maßnahmebeginn ist zu kurzfristig angesetzt. Um für die Vorbereitung des Dienstvertrags eine ausreichende Bearbeitungszeit zu gewährleisten, muss ein Antrag durch eine Hilfskraft mindestens x Wochen vor Vertragsbeginn beim Dezernat 4 eingereicht worden sein. Bitte wählen Sie einen späteren Vertragsbeginn."
   *    (Antragsteller) beim Annehmen der Maßnahme / Einreichen des Antrags: "Der geplante Vertragsbeginn darf nicht weniger als x Wochen in der Zukunft liegen. Bitte wenden Sie sich an das Dekanat in Ihrer Fakultät, so dass der Vertragsbeginn dort angepasst werden kann."
   */
  const minWochen = config.getValue("EINSTELLUNG_PUFFER_WOCHEN")[massnahme.m_artid]
    || config.getValue("EINSTELLUNG_PUFFER_WOCHEN_DEFAULT");
  const istVortrag = VERTRAGSARTEN_VORTRAG.includes(massnahme.m_artid);
  const pufferUnterschritten = massnahme.m_aenderung || istVortrag ? false : moment(massnahme.m_gueltigab).subtract(minWochen, "week").isBefore(moment());
  const inVergangenheit = moment(massnahme.m_gueltigab).isBefore(moment(), "day");

  if (!VERTRAGSARTEN_LEHRAUFTRAG.includes(massnahme.m_artid)) {
    // Bei Lehrauftrag keine Hinweismeldung.
    if (user.ro_id == ROLLEN.ROLLE_DEKANAT) {
      if ((!massnahme.antrag || !massnahme.antrag.a_id) && massnahme.m_veroeffentlicht && pufferUnterschritten) {
        const minDatum = formatDate(moment().add(minWochen, "week").add(1, "day"));
        messages.push(`Der Maßnahmebeginn ist zu kurzfristig angesetzt. Um für die Vorbereitung des Vertrags eine ausreichende Bearbeitungszeit zu gewährleisten, muss ein Antrag durch eine Hilfskraft mindestens ${ minWochen } Wochen vor Vertragsbeginn beim Dezernat 4 eingereicht worden sein. Bitte wählen Sie einen späteren Vertragsbeginn (nach dem ${ minDatum }).`);
      }
    } else if (user.ro_id == ROLLEN.ROLLE_ANTRAGSTELLER) {
      if (pufferUnterschritten) {
        messages.push(`Der geplante Vertragsbeginn (${ formatDate(massnahme.m_gueltigab) }) darf nicht weniger als ${ minWochen } Wochen in der Zukunft liegen. Bitte wenden Sie sich an das Personaldezernat Hilfskräfte und Lehraufträge, so dass der Vertragsbeginn dort angepasst werden kann.`);
      } else {
        if (inVergangenheit) {
          messages.push(`Der geplante Vertragsbeginn (${ formatDate(massnahme.m_gueltigab) }) liegt in der Vergangenheit. Der Vertrag kann nicht abgeschlossen werden. Bitte wenden Sie sich an Ihre Ansprechperson in Ihrer Einrichtung.`);
        }
      }
    }
  }

  /*
   *Validierung 2 Feiertag NUR für Antragstyp = NEU
   *beim Speichern der Maßnahme, sofern das Flag "veröffentlicht" = True ist
   *beim Annehmen einer Maßnahme in einen Antrag (Aktion: "Antrag stellen" nach Auswahl der Maßnahme)
   *beim erstmaligen Einreichen eines Antrags (Eingangsdatum noch leer)
   *geplanter Vertragsbeginn (bei mehreren NUR der früheste!) darf NICHT auf einem Wochenende oder Feiertag liegen. Es muss der erste Arbeitstag im Monat gewählt sein.
   *Hinweismeldung im Fehlerfall: "Der geplante Vertragsbeginn fällt auf ein nicht zulässiges Datum. Bitte wenden Sie sich an das Dekanat in Ihrer Fakultät, so dass der Vertragsbeginn dort angepasst werden kann."
   */

  if (messages.length) {
    return { nachricht: messages.join(" "), details: [] };
  }
};

export const validateVertrag = async(massnahmen, state, props) => {
  // Gültige Vertragsarten abhängig vom Hochschulabschluss prüfen

  if (!_.isArray(massnahmen)) {
    massnahmen = [massnahmen]; // nur eine Massnahme angegeben
  }

  // Bei Vortrag/Kolloquium steht die Vertragsart bereits fest, kein Hochschulabschluss nötig.
  massnahmen = massnahmen.filter(m => !VERTRAGSARTEN_VORTRAG.includes(m.m_artid));
  if (!massnahmen.length) {
    return [];
  }

  let { studiendaten } = state;

  if (!studiendaten) {
    // Studiendaten nicht übergeben
    try {
      const result = await apifetch("/api/shkperson/werdegang");
      studiendaten = result && result.data[0];
    } catch(err) {
      return [{
        text: err.response && (err.response.nachricht || err.response.um)
          || "Es ist ein Fehler aufgetreten.",
      }];
    }
  }

  if (!studiendaten || !studiendaten.pwg_pfaid) {
    return [{ text: `So lange Ihr Hochschulabschluss nicht in den Persönlichen Angaben eingegeben wurde, können die gültigen Vertragsarten nicht ermittelt werden.` }];
  }

  const { k_pfaartLookup } = props;
  const checks = {
    "3c5be90a-df5e-4df4-a4d9-b66e3b490d21": { name: "WHK FH", pfa_arten: ["040", "041", "042", "031"] },
    "e019a20a-9a10-46ed-9c04-73f434a024de": { name: "SHK", pfa_arten: ["050"] },
    "f15967e7-78de-4d6c-8450-6cdbfb2a5a61": { name: "WHK", pfa_arten: ["010", "020", "030", "031", "032", "033", "034"] },
  };

  const result = [];
  _.map(massnahmen, (m, index) => {
    const check = checks[m.m_artid];
    const svakey = k_pfaartLookup[studiendaten.pwg_pfaid].pfa_svakey;
    if (check.pfa_arten.indexOf(svakey) < 0) {
      // Vertragsart nicht erlaubt
      const allowed = [];
      _.map(Object.keys(checks), key => {
        if (checks[key].pfa_arten.indexOf(svakey) >= 0) {
          allowed.push(checks[key].name);
        }
      });
      result.push({ index: index, text: `Die Vertragsart ${ check.name } ist aufgrund Ihres Hochschulabschlusses nicht möglich (erlaubt: ${ allowed.join(", ") }). Insbesondere bei einem Wechsel der Vertragsart (z. B. SHK auf WHF) kann auch ein Abschlussdatum, welches in der Zukunft liegt, angegeben werden.` });
    }
  });
  return result;
};

// Feldlisten der für die Details (separat, weil Spezialeditoren verwendet werden sollen)
export const schemaArbeitszeitMn = {
  property: "massnahmepaz",
  table: [
    {
      field: "mp_gueltigab",
      display: "Gültig ab",
      type: "date",
      hideFromDetail: true,
      default: defaultVon,
    },
    {
      field: "mp_gueltigbis",
      display: "Gültig bis",
      type: "date",
      hideFromDetail: true,
      default: defaultBis,
    },

    /*
     *{
     *  field: 'mp_prozent',
     *  display: 'Anteil Arbeitszeit',
     *  type: 'number',
     *  hideFromDetail: keinTarif_additional
     *},
     */
    {
      field: "mp_std",
      display: "Wochenstunden",
      type: "number",
      required: true,
      // hideFromDetail: istTarif_additional,
      validate: value => {
        let bad = true;

        /*
         *if (value && String(value).indexOf(',')>=0) {
         *  value=value.replace(",",".")
         *}
         */
        try {
          bad = !_.isNumber(value) || parseFloat(value) < 1 || parseFloat(value) > 19 || (String(value).indexOf(".") > 0 && [1, 2].indexOf(String(value).split(".")[1].length) < 0);
        } catch {
          // Böse Eingabe
        }
        if (bad) {
          return "Die Wochenstunden müssen zwischen 1 und 19 Stunden betragen (max. 2 Dezimalstelllen)";
        }
      },
    },
    {
      field: "dateRange",
      display: "dateRange",
      specialEditor: true,
    },
  ],
};

export const ArbeitszeitRenderMethods = {
  mp_std: (value, obj) => formatNumber(parseFloat(obj.mp_std)) + " Std.",
  mp_prozent: (value, obj) => formatNumber(parseFloat(obj.mp_prozent)) + "%",
  dateRange: (value, obj) => dateRange(obj.mp_gueltigab, obj.mp_gueltigbis),
};

export const schemaTaetigkeitMn = {
  property: "massnahmetaetigkeit",
  table: [
    {
      field: "mt_taetigkeiten",
      type: "multiselect",
      inject: ["k_taetigkeitLookup", "additionalInjects"],
      choices: (lookup, entity) => {
        if (lookup && entity && entity.m_artid) {
          return _.map(_.filter(lookup, l => l.tat_artid == entity.m_artid), l => {
            return { value: l.tat_id, label: `${ l.tat_bez }` };
          });
        }
      },
      selector: lookup => id => id && lookup && lookup[id].tat_bez,
      display: "Tätigkeit",
    },
    {
      field: "mt_text",
      display: "Sonstiges",
      maxlength: 200,
    },
  ],
};
export const TaetigkeitRenderMethods = {
  mt_taetigkeiten: {
    inject: "k_taetigkeitLookup",
    generator: lookup => taetigkeitArr => {
      return _.map(taetigkeitArr, taetigkeitId => {
        return <div className='padding-t-10' key={taetigkeitId}>{lookup && lookup[taetigkeitId].tat_bez}</div>;
      });
    },
  },
};
export const schemaFinanzierungMn = {
  property: "massnahmefinanz",
  renderEditorRow: function(item) {
    const row = [];
    row.push(<td key='date' width='25%'>{`${ formatDate(item.mfi_gueltigab) } - ${ item.mfi_gueltigbis ? formatDate(item.mfi_gueltigbis) : "" }`}</td>);
    row.push(<td key='konto'>{`${ formatKonto({ kostenstelle: item.mfi_kostenstelle, budgetkonto: item.mfi_budgetkonto }) }`}</td>);
    row.push(<td key='prozent'>{parseFloat(item.mfi_prozent) != 100 ? `${ formatNumber(item.mfi_prozent || 0) }%` : ""}</td>);
    return row;
  },
  table: [
    {
      field: "mfi_gueltigab",
      display: "Gültig ab",
      type: "date",
      hideFromDetail: true,
      default: defaultVon,
    },
    {
      field: "mfi_gueltigbis",
      display: "Gültig bis",
      type: "date",
      hideFromDetail: true,
      default: defaultBis,
    },
    {
      field: "mfi_kostenstelle",
      display: "Kostenstelle",
      maxlength: 9,
      validate: value => {
        if (value && [5, 9].indexOf(String(value).length) < 0) {
          return "Die Kostenstelle ist ungültig (5 oder 9 Stellen erwartet)";
        }
      },
    },
    {
      field: "mfi_budgetkonto",
      display: "Budgetkonto",
      maxlength: 9,
    },
    {
      field: "mfi_prozent",
      display: "Prozent",
      type: "percent",
      default: 100,
    },
    {
      field: "dateRange",
      display: "dateRange",
      specialEditor: true,
    },
  ],
};
export const FinanzierungRenderMethods = {
  mfi_prozent: (value, obj) => `${ formatNumber(obj.mfi_prozent || 0) }%`,
  mfi_kostenstelle: (value, obj) => `${ formatKonto({ kostenstelle: obj.mfi_kostenstelle, budgetkonto: obj.mfi_budgetkonto }) }`,
  dateRange: (value, obj) => dateRange(obj.mfi_gueltigab, obj.mfi_gueltigbis),
};

export const schemaQualizielMn = {
  property: "massnahmequaliziel",
  table: [
    {
      field: "mq_qualiziele",
      lookup: "k_qualiziel.multiselect.qz_id.qz_bez",
      display: "Qualifikationsziel",
    },
    {
      field: "mq_text",
      display: "Sonstiges",
      maxlength: 200,
    },
  ],

};
export const QualizielRenderMethods = {
  mq_qualiziele: {
    inject: "k_qualizielLookup",
    generator: lookup => qualizielArr => {
      return _.map(qualizielArr, qualizielId => {
        return <div className='padding-t-10' key={qualizielId}>{lookup && lookup[qualizielId].qz_bez}</div>;
      });
    },
  },
};

export const formatWochentage = array => {
  // Als englische Kürzel ankommende Wochentage formatieren
  const map = {
    mon: { pos: 1, name: "Montag" },
    tue: { pos: 2, name: "Dienstag" },
    wed: { pos: 3, name: "Mittwoch" },
    thu: { pos: 4, name: "Donnerstag" },
    fri: { pos: 5, name: "Freitag" },
    sat: { pos: 6, name: "Samstag" },
    sun: { pos: 7, name: "Sonntag" },
    nvb: { pos: 8, name: "Nach Vereinbarung" },
    blk: { pos: 9, name: "Block" },
  };
  return _.join(_.map(_.sortBy(_.map(array, item => _.get(map, item, null)), x => x.pos), item => item.name).filter(_.identity), ", ");
};

export const schemaLehrauftragMn = {
  table: [
    {
      field: "ml_bezeichnung",
      display: "Bezeichnung des Lehrauftrages",
    },
    {
      field: "ml_istkuenstlerisch",
      display: "Es handelt sich um einen künstlerischen oder publizistischen Lehrauftrag",
      type: "checkbox",
    },
    {
      field: "ml_veranstaltungsart",
      display: "Veranstaltungstyp",
    },
    {
      field: "ml_veranstaltungstyp",
      display: "Veranstaltungsart",
    },
    {
      field: "ml_wochentage",
      display: "Wochentage",
      selector: value => formatWochentage(value),
    },
    {
      field: "ml_begruendung",
      display: "Ausführliche Begründung",
    },
    {
      field: "ml_lehrauftragsart",
      display: "Vergütung",
    },
    {
      field: "ml_klebegruendung",
      display: "Begründung der besonderen Bedeutung",
    },
    {
      field: "ml_stundensatz",
      display: "Stundensatz",
    },
    {
      field: "ml_fahrtkosten",
      display: "Fahrtkosten",
      type: "money",
    },
    {
      field: "ml_gesamtstd",
      display: "Gesamtstunden im Semester",
    },
    {
      field: "ml_sws",
      display: "Deputatsleistung in LVS",
      shouldShow: mne => mne.ml_gesamtstd,
    },
  ],
};

export const LehrauftragRenderMethods = {

};

export const schemaStellenaustarifMn = {
  property: "massnahmetarif",
  table: [
    {
      field: "mtf_auskennz",
      display: "Ausschreibungskennz.",
      type: "text",
    },
    {
      field: "mtf_ohneaus",
      type: "checkbox",
      display: "Ausschreibungsverzicht",
    },
    {
      field: "mtf_ohneausgrund_kogid",
      display: "Gründe für den Verzicht",
      type: "multiselect",
      hideFromDetail: mtf => !mtf.mtf_ohneaus || !mtf.mtf_ist_wiss,
    },
    {
      field: "mtf_ohneausgrund",
      display: "Erläuterung zum Verzicht",
      type: "text",
      hideFromDetail: mtf => !mtf.mtf_ohneaus,
    },
    {
      field: "mtf_bewerber_m",
      display: "Anzahl Bewerber (männlich)",
      type: "number",
      numeralFormat: "0",
      hideFromDetail: mtf => mtf.mtf_ohneaus,
    },
    {
      field: "mtf_bewerber_w",
      display: "Anzahl Bewerber (weiblich)",
      type: "number",
      numeralFormat: "0",
      hideFromDetail: mtf => mtf.mtf_ohneaus,
    },
    {
      field: "mtf_bewerber_d",
      display: "Anzahl Bewerber (divers)",
      type: "number",
      numeralFormat: "0",
      hideFromDetail: mtf => mtf.mtf_ohneaus,
    },
  ],
};

export const schemaAufgabenbetarifMn = {
  property: "massnahmetarif",
  table: [
    {
      field: "mtf_einsatz_als",
      display: "Einsatz als.",
      type: "text",
    },
    {
      field: "mtf_aufgabenbeschreibung",
      type: "text",
      display: "Beschreibung",
    },
  ],
};

export const StellenaustarifRenderMethods = {
  mtf_ohneausgrund_kogid: {
    inject: ["k_ohneaus_grundLookup"],
    generator: lookup => array => lookup && _.map(array, (id, i) => <li key={i}>{lookup[id]?.kog_kbez}</li>),
  },
};

export const schemaDeputatMn = {
  property: "massnahmedeputat",
  table: [
    {
      field: "md_lehreinheit",
      display: "Lehreinheit",
      type: "text",
    },
    {
      field: "md_adbid",
      display: "Kategorie nach LVV",
      type: "select",
      inject: "k_amtsdienstbezLookup",
      selector: lookup => adbid => lookup && lookup[adbid]?.adb_bez || "",
      noSort: true,
    },
    {
      field: "dateRange",
      display: "dateRange",
      specialEditor: true,
    },
    {
      field: "md_sws",
      display: "Anteiliges persönliches Deputat (in LVS)",
    },
    {
      field: "md_mind_dmiid",
      display: "Minderungsgrund",
      type: "select",
      inject: "k_deputatminderung",
      selector: list => dmiid => _.get(_.find(list, item => item.dmi_id === dmiid), "dmi_bez"),
      choices: list => (list || []).map(item => ({ value: item.dmi_id, label: item.dmi_bez })),
      className: "card-checkbox-body card-checkbox-last",
    },
  ],
};

export const DeputatRenderMethods = {
  md_sws: value => value ? value + " LVS" : "",
  dateRange: (value, obj) => dateRange(obj.md_gueltigab, obj.md_gueltigbis),
  md_mind_dmiid: {
    inject: "k_deputatminderungLookup",
    generator: lookup => (value, obj) => lookup && obj.md_mind_sws ? `(-${ obj.md_mind_sws } LVS ${ lookup[value]?.dmi_bez || "" })` : "",
  },
};

const schemas = [schemaMassnahme, schemaArbeitszeitMn, schemaTaetigkeitMn, schemaFinanzierungMn, schemaQualizielMn, schemaLehrauftragMn, schemaStellenaustarifMn, schemaDeputatMn];
_.map(schemas, s => setupLookups(s));

// Auswahl Vertragsart einschränken
const vert = schemaMassnahme.table.find(f => f.field == "m_artid");
vert.choices = function(lookup) {
  return _.map(
    _.filter(lookup, l => VERTRAGSARTEN_SHK.indexOf(l.art_id) >= 0 || VERTRAGSARTEN_LEHRAUFTRAG.indexOf(l.art_id) >= 0 || VERTRAGSARTEN_TARIF.includes(l.art_id)),
    l => ({ value: l.art_id, label: `${ l.art_kbez }` }),
  );
};
// PBP-2623 Hierarchie aufbauen, damit Tarif Vertragsarten zusammengefasst ausgewählt werden können
vert.filter.filterbox.choices = function(lookup) {
  let choices = _.map(
    _.filter(lookup, l => VERTRAGSARTEN_SHK.indexOf(l.art_id) >= 0 || VERTRAGSARTEN_LEHRAUFTRAG.indexOf(l.art_id) >= 0),
    l => ({ art_kbez: l.art_kbez, depth: 0 }),
  );
  // Tarif: eigentliche Vertragsart steht erst später im Antrag fest, daher nur nach mtf_ist_wiss unterscheiden
  const tarifarten = [
    { art_kbez: "Wiss MA", depth: 1 },
    { art_kbez: "TuV MA", depth: 1 },
  ];
  const parent = { depth: 0, art_kbez: "Tarifbeschäftigte", children: tarifarten };
  choices.push(parent);
  choices = choices.concat(tarifarten);
  return choices;
};
vert.filter.filterbox.hierarchy = {
  depth: art => art.depth,
  display: art => art.art_kbez,
};

// Bei Tarif-Vertragsarten statt Lookup-Bezeichnung Wiss MA / TuV MA anzeigen bzw. filtern, da die eigentliche Vertragsart erst später im Antrag festgelegt wird
const tarifBezeichnung = entity => (_.find(entity.massnahmetarif, x => x?.mtf_ist_wiss) ? "Wiss MA" : "TuV MA");

vert.selector = lookup => (value, entity) => {
  if (VERTRAGSARTEN_TARIF.includes(value)) {
    return tarifBezeichnung(entity);
  }
  return lookup && lookup[value] && lookup[value].art_kbez;
};

vert.filter.subquery = {
  inject: ["k_vertragsartLookup"],
  fields: { m_artid: { alias: "art_kbez" } },
  generator: lookup => entity => {
    if (VERTRAGSARTEN_TARIF.includes(entity.m_artid)) {
      return { art_kbez: tarifBezeichnung(entity) };
    }
    return lookup && lookup[entity.m_artid];
  },
};


function updateAntragMassnahmen(mn) {
  return inline({ antragList: "shallow", antragLookup: "shallow" }, ({ antragList, antragLookup }) => {
    if (antragList) {
      for (let i = 0; i < antragList.length; ++i) {
        let antrag = antragList[i];
        const mnIndex = _.findIndex(antrag.massnahmen, antragMn => antragMn.m_id === mn.m_id);
        if (mnIndex !== -1) {
          antrag = _.cloneDeep(antrag);
          antragList[i] = antrag;
          antragLookup[antrag.a_id] = antrag;
          antrag.massnahmen[mnIndex] = mn;
        }
      }
    }
  });
}

const desc = {
  id: "m_id",
  name: "massnahme",
  endpoint: "/api/shkantrag/massnahme",
  schema: schemaMassnahme,
  breadcrumbs: {
    list: "Maßnahme",
    detail: n => schemaMassnahme.toUserString(n),
  },
  extraActions: {
    saveMassnahmeDetail,
    deleteMassnahmeDetail,
  },
  noListFromCurrent: true,
  onSaved: updateAntragMassnahmen,
};
const massnahmeApi = api(desc);

function saveMassnahmeDetail(entity, data, arrayinfo) {
  return asyncOperation("currentMassnahme", async dispatch => {
    // Validierungen
    const schema = schemas.find(s => s.property == arrayinfo.property);
    const details = [];
    _.map(Object.keys(data), key => {
      const field = schema.table.find(f => f.field == key);
      if (field) {
        if (field.unformat) {
          data[key] = field.unformat(data[key]);
        }
        const message = field.validate && field.validate(data[key]);
        if (message) {
          details.push({ attribut: field.field, nachricht: message });
        }
      }
    });
    if (details.length) {
      const e = Error();
      e.response = { nachricht: "Es sind Fehler bei der Validierung aufgetreten", details: details };
      throw e;
    }

    // Vorherigen Stand des Detail-Arrays ermitteln
    const array = arrayinfo.maxrows == 1 ? [] : entity[arrayinfo.property] || [];

    if (_.isEmpty(data)) {
      // Nichts zu tun
    } else {
      // Wenn Daten eine ID enthalten, muss der Satz im bestehenden Array aktualisiert werden
      if (data[arrayinfo.id]) {
        // ID gefüllt, Satz suchen
        const idx = array.findIndex(x => x[arrayinfo.id] == data[arrayinfo.id]);
        if (idx >= 0) {
          // Record gefunden, aktualisieren
          _.assign(array[idx], data);
        } else {
          // Nicht gefunden (?), anhängen
          array.push(data);
        }
      } else {
        array.push(data);
      }
    }

    entity[arrayinfo.property] = array;

    const url = `/api/shkantrag/massnahme`;
    await apifetch(url, {
      method: "POST",
      body: entity,
    });
    dispatch(massnahmeApi.updateInStore(entity.m_id, entity));
    dispatch(updateAntragMassnahmen(entity));
  });
}

function deleteMassnahmeDetail(entity, id, arrayinfo) {
  return asyncOperation("currentMassnahme", async dispatch => {
    if (_.isEmpty(id)) {
      // Nichts zu tun
    } else {
      let array = entity[arrayinfo.property] || [];
      array = array.filter(x => x[arrayinfo.id] != id);
      if (_.isEmpty(array)) {
        array = null;
      }
      entity[arrayinfo.property] = array;

      const url = `/api/shkantrag/massnahme`;
      await apifetch(url, {
        method: "POST",
        body: entity,
      });
      dispatch(massnahmeApi.updateInStore(entity.m_id, entity));
      dispatch(updateAntragMassnahmen(entity));
    }

    // await saveMassnahmeDetail(entity,null,arrayinfo)
  });
}

export default massnahmeApi;

const descVorgesehen = {
  id: "m_id",
  name: "massnahmevorgesehen",
  endpoint: "/api/shkantrag/massnahmevorgesehen", // spezieller Endpunkt, der nur die eigenen Maßnahmen liefert
  schema: schemaMassnahme,
  breadcrumbs: {
    list: "Antrag",
    detail: n => schemaMassnahme.toUserString(n),
  },
};
export const massnahmevorgesehenApi = api(descVorgesehen);

export function formatMassnahme(massnahme, props) {
  // Definierte Stringdarstellung für Maßnahme erzeugen
  const istTarif = istArtTarifvertrag(massnahme.m_artid);

  const wissPrefix = massnahme?.mtf_ist_wiss ? "Wiss. " : "";

  const { k_einrichtungLookup, k_antragstypLookup, k_vertragsartLookup } = props;

  const einrichtung = k_einrichtungLookup && k_einrichtungLookup[massnahme.m_eid];
  const fakultaet = k_einrichtungLookup && k_einrichtungLookup[massnahme.m_fakultaet_eid];
  const vertragsart = istTarif ? wissPrefix + "Tarifbeschäftigte/r" : k_vertragsartLookup && k_vertragsartLookup[massnahme.m_artid].art_kbez;
  const vertragstyp = k_antragstypLookup && massnahme.m_katid && k_antragstypLookup[massnahme.m_katid].kat_kbez;
  const bezeichnung = massnahme.ml_bezeichnung;

  const zeitraum = (massnahme.m_gueltigab ? `vom ${ formatDate(massnahme.m_gueltigab) }` : "") + (massnahme.m_gueltigbis ? ` bis ${ formatDate(massnahme.m_gueltigbis) }` : "");
  const umfang = _.map(massnahme.massnahmepaz, m => istTarif
    ? `${ m.mp_prozent }%`
    : `${ m.mp_std } Wochenstunden`).join(" / ");

  const text = `${ vertragsart }, ${ massnahme.m_artid == VERTRAGSARTEN_LEHRAUFTRAG ? bezeichnung + ", " : "" } ${ zeitraum }${ vertragstyp ? ", " + vertragstyp : "" }, ${ fakultaet && fakultaet.e_kbez } (${ einrichtung && einrichtung.e_kbez }) ${ massnahme && (massnahme.m_asp || "") }${ umfang ? ", Beschäftigungsumfang: " + umfang : "" }`;

  return text;
}

export function formatAntrag(antrag, props) {
  // Definierte Stringdarstellugn für Maßnahme erzeugen
  const { k_einrichtungLookup, k_vertragsartLookup, k_antragstatusLookup } = props;
  const istTarif = istArtTarifvertrag(antrag.a_artid);

  const wissPrefix = antrag?.atd_ist_wiss ? "Wiss. " : "";

  const fakultaet = k_einrichtungLookup && k_einrichtungLookup[antrag.a_fakultaet_eid];
  const vertragsart = istTarif ? wissPrefix + "Tarifbeschäftigte/r" : k_vertragsartLookup && k_vertragsartLookup[antrag.a_artid].art_kbez;
  const status = k_antragstatusLookup && k_antragstatusLookup[antrag.a_asid].as_bez;

  const text = `${ antrag.a_nr || "" } ${ vertragsart }, ${ fakultaet && fakultaet.e_kbez } - ${ status }`;

  return text;
}

export function formatKonto({ kostenstelle, budgetkonto }) {
  /*
   * Bei einer 9stelligen Kostenstelle wird die Anzeige des Budgetkontos NICHT ergänzt.
   * Bedeutet: WENN Kostenstelle 5stellig, dann konkatinieren KST + Budgetkonto
   * WENN Kostenstelle 9stellig, nur diese anzeigen.
   */
  if (!kostenstelle) {
    return "";
  }
  if (String(kostenstelle).length == 5) {
    return `${ kostenstelle }${ budgetkonto || "" }`;
  }
    // eigentlich: "wenn 9-stellig dann alleine anzeigen"
  return kostenstelle;
}

export function renderKurzfristinfo(massnahme, kennzeichen, k_vertragsgrundLookup, mitWell) {
  /*
   * Liste von Gründen zu Kurzfristigkeit zu einer Maßnahme ausgeben
   * In der Maßnahme in Vertragsdaten udn Arbeitszeit, im Antragsdeckblatt im analog
   */
  if (!k_vertragsgrundLookup) {
    return <Spinner />;
  }

  const info = _.filter(massnahme.massnahmevertragsgrund || [], mv => mv.mvg_kennzeichen == kennzeichen);

  const renderList = info => {
    // List-Items ausgeben
    return _.map(
      info,
      mvg => {
        const arr = _.map(mvg.mvg_vertragsgrund, (g, i) => {
          if (k_vertragsgrundLookup[g]) {
            const mvginfo = mvg.mvg_vertragsgrundinfo ? mvg.mvg_vertragsgrundinfo.filter(i => i.id === g).map(r => r.value).join(", ") : null;
            return <li key={i}>{k_vertragsgrundLookup[g].kvg_text + (mvginfo ? " (" + mvginfo + ")" : "")} <br /></li>;
          }
        });
        if (mvg.mvg_text) {
          arr.push(<li key='mvg_text'>{mvg.mvg_text}</li>);
        } // Freitext hinzufügen
        return arr;
      },
    );
  };

  // Gründe für Kurzfristigkeit (bezogen auf Vertragsdauer oder Stundenanzahl) ausgeben
  if (_.isEmpty(info)) {
    return null;
  }
  const content = <span><b>{`Der Vertrag für die o.g. Hilfskraft soll aus folgenden Gründen abgeschlossen werden,
                          ${ kennzeichen == "S"
        ? "obwohl die wöchentliche Arbeitszeit unter 5 Stunden liegt"
        : "obwohl die Gesamtdauer unter 12 Monaten liegt" }`}:</b>
  <br /><br />
  <ul>{renderList(info)}</ul></span>;
  if (mitWell) {
    return <Well>{content}</Well>;
  }
  return content;
}

export function getEigen(mn, typ, filter, props) {
  // Eigenschaften eines Typs zu einer Maßnahme liefern
  return mn && _.filter(mn.eigenschaften, e => e.mne_metid == typ && (!_.isFunction(filter) || filter(e, props)));
}

export function getPBPLink(m, user) {
  const location = window.location;
  let origin = location.origin;

  if (user && user.serverinfo) {
    origin = user.serverinfo.pbpurl;
  } else {
    // Keine Serverinfo gefunden, raten
    if (origin.indexOf("e-portal.uni-wuppertal") >= 0) {
      origin = origin.replace("e-portal", "pbp");
    } else if (origin.indexOf("e-portal.prounix") >= 0) {
      origin = origin.replace("e-portal", "pbp");
    } else if (origin.indexOf("localhost:4001") >= 0) {
      origin = "https://pbp.prounix.local";
    } else {
      return "";
    }
  }

  return `${ origin }/massnahme/${ m.m_id }`;
}
